'use client'

import { toast } from '@/components/common/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useContactMutation } from '@/hooks/react-query/contact/use-contact.mutation'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent } from '@/lib/gtm/type'
import { AffiliateTracker } from '@/lib/tracking/affiliate-tracker'
import { validatePhone } from '@/lib/utils/validation'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import * as z from 'zod'

// Schema definition
export const CxpContactForm = () => {
  const { mutateAsync: sendContact, isPending } = useContactMutation()
  const t = useTranslations('cxp_contact_form')

  // Đưa các message, label, ... vào sau khi đã có t
  const contactFormSchema = z.object({
    quantity: z.string().min(1, t('quantity_required')),
    name: z.string().min(1, t('name_required')),
    phone: z.string().refine(
      (value) => {
        const phoneValidate = validatePhone(value)
        return phoneValidate.valid
      },
      {
        message: t('invalid_phone'),
      }
    ),
  })

  type ContactFormData = z.infer<typeof contactFormSchema>

  // Constants
  // const PRODUCT_OPTIONS = [
  //   { value: 'Áo', label: 'Áo' },
  //   { value: 'Quần', label: 'Quần' },
  //   { value: 'Phụ kiện', label: 'Phụ kiện' },
  // ]

  const QUANTITY_OPTIONS = [
    { value: 'Từ 1-5', label: t('quantity_1_to_5') },
    { value: 'Từ 6-19', label: t('quantity_6_to_19') },
    { value: 'Từ 20-50', label: t('quantity_20_to_50') },
    { value: 'Từ 51-100', label: t('quantity_51_to_100') },
    { value: 'Từ 101-500', label: t('quantity_101_to_500') },
    { value: 'Từ 501-1000', label: t('quantity_501_to_1000') },
    { value: 'Trên 1000', label: t('quantity_above_1000') },
  ]

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      quantity: '',
      name: '',
      phone: '',
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    // getValues,
    setValue,
    reset,
    watch,
  } = form

  // const formValues = getValues()
  const affiliateData = AffiliateTracker.getInstance().getTrackingData()
  const trackEvent = useTrackEvent()

  const onSubmit = async (data: ContactFormData) => {
    try {
      await sendContact({
        ...data,
        type: 'coolxprint',
        // products: data?.products?.map((product) => product?.value || ''),
        utm_source: affiliateData?.aff_channel || '',
        utm_medium: affiliateData?.utm_medium || '',
        utm_campaign: affiliateData?.utm_campaign || '',
        utm_term: affiliateData?.utm_term || '',
        utm_content: affiliateData?.utm_content || '',
      })

      toast.message({
        title: t('success_title'),
        message: t('success_message'),
      })
      reset()

      trackEvent({
        event: ETrackingEvent.Conversion,
        send_to: 'AW-11221863705/0iymCNa9oq4YEJmagOcp',
      })

      trackEvent({
        pageType: 'Confirmation',
      } as never)

      trackEvent({
        event: ETrackingEvent.SubmitOct,
        // oct_thankyou_email: data.email,
        oct_thankyou_phone: data.phone,
      })

      // const products = [
      //   {
      //     name: data?.products
      //       ?.map((product) => product?.value || '')
      //       ?.join(', '),
      //   },
      // ]
      const trackingData = {
        event: ETrackingEvent.FormSubmissionSuccess,
        serviceOrder: 'true',
        // products,
      }
      trackEvent(trackingData)
      trackEvent({ event: ETrackingEvent.Remarketing })

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.message({
        title: t('fail_title'),
        message: t('fail_message'),
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h2 className='mb-6 text-2xl font-bold text-primary-400'>
        {t('form_title')}
      </h2>

      <div className='space-y-4'>
        <div>
          <label className='mb-2 block text-sm text-neutral-900'>
            {t('name_label')} <span className='text-error-500'>*</span>
          </label>
          <Input
            {...register('name')}
            placeholder={t('name_placeholder')}
            error={errors.name}
          />
        </div>

        <div>
          <label className='mb-2 block text-sm text-neutral-900'>
            {t('phone_label')} <span className='text-error-500'>*</span>
          </label>
          <Input
            {...register('phone')}
            placeholder={t('phone_placeholder')}
            error={errors.phone}
          />
        </div>

        <div>
          <label className='mb-2 block text-sm text-neutral-900'>
            {t('quantity_label')} <span className='text-error-500'>*</span>
          </label>
          <Select
            placeholder={t('quantity_placeholder')}
            options={QUANTITY_OPTIONS}
            value={watch('quantity')}
            onValueChange={(value) => {
              setValue('quantity', value)
            }}
            error={errors.quantity}
          />
        </div>

        {/* <div>
          <label className='mb-2 block text-sm text-neutral-900'>
            Sản phẩm bạn chọn
          </label>

          <MultipleSelector
            defaultOptions={PRODUCT_OPTIONS}
            placeholder='Chọn sản phẩm'
            emptyIndicator={
              <p className='text-center text-sm leading-10 text-gray-600'>
                Không có kết quả{' '}
              </p>
            }
            className='max-w-full'
            error={errors.products}
            value={
              formValues?.products?.map((product) => ({
                label: product?.label || '',
                value: product?.value || '',
              })) || []
            }
            onChange={(value) => {
              setValue(
                'products',
                value.map((item) => ({
                  label: item.label,
                  value: item.value,
                }))
              )
            }}
          />
        </div>
        <div>
          <label className='mb-2 block text-sm text-neutral-900'>Email</label>
          <Input
            {...register('email')}
            placeholder='Nhập email'
            error={errors.email}
          />
        </div> */}
        <Button type='submit' className='w-full' disabled={isPending}>
          {isPending ? t('submit_sending') : t('submit')}
        </Button>
      </div>
    </form>
  )
}
